import Button from '@pinecast/common/Button';
import Label from '@pinecast/common/Label';
import Select from '@pinecast/common/Select';
import {P} from '@pinecast/common/Text';
import {GQLButtonLabel, useManagedMutation} from '@pinecast/gql';
import {FormattedMessage} from '@pinecast/i18n';
import {documentLocale, languageOptions} from '@pinecast/i18n/supportedLocales';
import gql from 'graphql-tag';
import * as React from 'react';

const SET_TIPPER_LANGUAGE = gql`
  mutation SetTipperLanguage($language: String!) {
    setTipperLanguage(language: $language) {
      language
    }
  }
`;

type SetLanguageData = {setTipperLanguage: {language: string} | null};
type SetLanguageVariables = {language: string};

const SettingsPageLanguage = () => {
  const setLanguage = useManagedMutation<SetLanguageData, SetLanguageVariables>(
    SET_TIPPER_LANGUAGE,
  );

  // No query for either half: the list is `i18n.json` and the current value is
  // the `<html lang>` the server already resolved for this tipper.
  const [current] = React.useState(documentLocale);
  const [selected, setSelected] = React.useState(current);
  // Not `setLanguage.pending` — that goes false as soon as the mutation
  // resolves, which would re-enable the control for the gap before the reload
  // takes the page away. Cleared only on failure.
  const [saving, setSaving] = React.useState(false);

  const options = React.useMemo(() => languageOptions(current), [current]);

  return (
    <>
      <P>
        <FormattedMessage
          description="Copy explaining what the tip jar language setting covers"
          defaultMessage="Choose your preferred language for this dashboard and any emails we send you. This does not affect the language of the podcasts you subscribe to."
        />
      </P>
      {/* `Label` wraps this in a native <label> and clones the child with an
          `aria-labelledby` pointing at that text, which wins over `aria-label`
          in the accessible-name computation. An `aria-label` here would be
          both dead weight and a name that disagrees with the visible one. */}
      <Label
        text={
          <FormattedMessage
            description="Label for the tip jar language picker"
            defaultMessage="Language"
          />
        }
      >
        <Select
          disabled={saving}
          value={selected}
          options={options.map(language => ({
            key: language.code,
            label:
              language.name === language.nameLocal
                ? language.name
                : `${language.name} — ${language.nameLocal}`,
          }))}
          onChange={setSelected}
        />
      </Label>
      <GQLButtonLabel
        state={setLanguage}
        successMessage={
          <FormattedMessage
            description="Message shown once the language has been saved, while the page reloads into it"
            defaultMessage="Reloading in your new language…"
          />
        }
      >
        <Button
          $isPrimary
          disabled={selected === current || saving}
          pending={saving}
          onClick={async () => {
            setSaving(true);
            const result = await setLanguage.action({
              variables: {language: selected},
            });
            // Translations are compiled into the bundle rather than fetched,
            // so the page this is running in cannot switch language — the
            // reload is what picks up the new one.
            if (result) {
              window.location.reload();
              return;
            }
            setSaving(false);
          }}
        >
          <FormattedMessage
            description="Button that saves the chosen language"
            defaultMessage="Save language"
          />
        </Button>
      </GQLButtonLabel>
    </>
  );
};

export default SettingsPageLanguage;
